import createDOMPurify from 'dompurify';

/**
 * Sanitizacao do HTML dos posts, funcionando dos dois lados.
 *
 * O DOMPurify precisa de um DOM. No navegador ele pega o `window` global; no
 * Node, `DOMPurify.isSupported` e false e `sanitize()` devolve a string
 * INTACTA -- sem erro nenhum, em silencio. Como o conteudo do post entra por
 * `dangerouslySetInnerHTML` e o React nao revalida esse HTML na hidratacao, o
 * texto cru gerado no build ficaria no DOM do visitante. Ou seja: migrar para
 * geracao estatica desligaria a sanitizacao sem avisar.
 *
 * Aqui o ramo de servidor monta um DOM com jsdom.
 *
 * ⚠️ O especificador do import e uma VARIAVEL com `@vite-ignore` de proposito.
 * Com `await import('jsdom')` literal, o Rollup criava o chunk mesmo o codigo
 * sendo inalcancavel no cliente (o `import.meta.env.SSR` vira `false`, mas ele
 * nao poda o import dinamico): o build do navegador ganhou um asset de 5,5 MB
 * so de jsdom. Como variavel opaca, o import so e resolvido em tempo de
 * execucao -- no Node, durante a geracao estatica, e nunca no navegador.
 */

type Sanitizador = { sanitize: (html: string, config?: object) => string };

let instancia: Sanitizador | null = null;

/**
 * Prepara o sanitizador do lado do servidor. Chamar no `loader` da rota, que
 * roda antes do render. No navegador nao e necessario: a instancia se resolve
 * sozinha, de forma sincrona, na primeira chamada de `sanitizar`.
 */
export async function prepararSanitizador(): Promise<void> {
  if (instancia || !import.meta.env.SSR) return;
  const pacote = 'jsdom';
  const { JSDOM } = (await import(/* @vite-ignore */ pacote)) as typeof import('jsdom');
  // O tipo `DOMWindow` do jsdom nao satisfaz o `WindowLike` do dompurify, mas
  // em tempo de execucao tem tudo que ele usa.
  const janela = new JSDOM('').window as unknown as Parameters<typeof createDOMPurify>[0];
  instancia = createDOMPurify(janela) as Sanitizador;
}

export function sanitizar(html: string, config?: object): string {
  if (!instancia && !import.meta.env.SSR) {
    instancia = createDOMPurify as unknown as Sanitizador;
  }

  // No servidor sem preparo, devolver string vazia em vez do HTML cru: perder a
  // secao e um bug visivel; publicar conteudo nao sanitizado, nao.
  if (!instancia) return '';

  return instancia.sanitize(html, config);
}
